'use client';

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Ticket } from 'lucide-react';
import { useDockStack } from './PayDock';

/**
 * REGISTERING, FROM WHEREVER YOU HAVE READ TO
 * ===========================================
 *
 * On a desktop the registration panel is in the right column, beside the
 * artwork, and there is nothing to solve: it is on screen before you have read
 * anything. On a phone that column stacks underneath, so the panel lands below
 * the arrival instructions and the agenda, and the answer to "how do I sign
 * up" is somewhere around the fourth screen.
 *
 * So on a phone the action comes to the reader instead. A bar across the
 * bottom naming the ways in, because "Volunteer" being one of the answers is
 * how somebody learns that serving is part of this rather than a separate
 * thing to go and find, and a button that opens the same sheet the panel
 * opens.
 *
 * NO PRICE ON IT. Four ways in that cost different amounts cannot be honestly
 * reduced to one number on a bar this size, and the number was the half that
 * was wrong: it read the event's base price, which Flannel Fest does not
 * have, and printed "Free" on a page that charges a Student $10.
 *
 * IT OPENS ON "WHO", WHICH IS A STEP THE SHEET ALREADY HAD. The sheet has
 * always known how to ask who is coming; it skipped that step whenever the
 * page handed it a selection, which the panel always did. Coming in from here
 * hands it nothing, so the sheet asks the question itself. Nothing about the
 * flow is different on a phone: it is the same component and the same steps,
 * entered one step earlier.
 *
 * IT SHARES THE CART'S COLUMN. `PayDockHost` renders a flex column fixed to
 * the bottom of the window, and the cart docks into it. This docks into the
 * same one, so when somebody has an invoice open and a registration to start,
 * the two bars stack instead of covering each other. That column is portalled
 * to the widget's overlay host because the theme's stacking contexts break
 * `position: fixed` rendered in place, which is a problem already solved
 * there and not worth solving twice.
 *
 * IT GETS OUT OF THE WAY WHEN THE PANEL IS ON SCREEN. Scroll far enough and
 * the real panel arrives, with the same button on it. Two of those visible at
 * once is one too many, and the bar would be sitting over the panel's own
 * controls.
 *
 * AND IT LEAVES THE WAY IT CAME, which is downwards. Unmounting made it blink
 * out, and a thing that vanishes reads as a fault rather than as a thing that
 * has finished. It slides off the bottom instead, so the eye follows it to
 * where it went and finds the panel it handed over to. Which means it stays
 * mounted while it is away, and `inert` is what stops a bar nobody can see
 * from catching a tap meant for the page underneath.
 *
 * THE SLIDE AND THE FIRST APPEARANCE ARE ON DIFFERENT ELEMENTS ON PURPOSE.
 * An `animation` with `both` holds its final transform forever and outranks
 * anything a class sets, so an element that animates in cannot then be
 * transitioned out. The outer box owns the coming and going; the card inside
 * it plays its one-shot rise on mount. Neither can hold the other still.
 */
export function RegisterDock({
  label,
  ways,
  anchor,
  hidden = false,
  onOpen,
}: {
  /** The panel's own words, so the two buttons never disagree. */
  label: string;
  /**
   * The ways in, already trimmed to what fits — see `waysInSummary`, which is
   * also what guarantees the serving one is among them.
   */
  ways: { shown: string[]; more: number };
  /**
   * The registration panel. While it is on screen the bar stands down: the
   * panel says the same thing, and the bar would be covering it.
   */
  anchor: React.RefObject<HTMLElement | null>;
  /** Registration is not open, or the sheet already is. */
  hidden?: boolean;
  onOpen: () => void;
}) {
  const stack = useDockStack();
  // Starts down and rises, which is the right way round: a page opened at the
  // top wants the bar, and the observer takes it away a frame later on the
  // rarer load that begins at the panel.
  const [panelSeen, setPanelSeen] = useState(false);

  useEffect(() => {
    const el = anchor.current;
    // No panel to watch, or a browser without the observer: the bar stays, which
    // is the safe way to be wrong. Worst case it is one button too many.
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      // The observer always delivers an entry; if it ever did not, leaving
      // the bar up is the safe way to be wrong.
      ([entry]) => {
        if (entry) setPanelSeen(entry.isIntersecting);
      },
      // The bar itself covers the bottom of the window, so the panel only
      // counts as reached once it is properly on screen rather than sliding
      // under the thing that is about to disappear.
      { threshold: 0.15, rootMargin: '0px 0px -96px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [anchor]);

  // Outside a host there is nowhere to dock, and the page already has its
  // panel: better nothing than a bar rendered in the middle of the document.
  if (stack === undefined || !stack) return null;

  // Away rather than gone: still rendered, so it has something to slide with.
  const away = hidden || panelSeen;

  return createPortal(
    <div
      inert={away}
      aria-hidden={away}
      /* `translate`, not `transform`. Tailwind v4 moves things with the
         `translate` property, so a transition naming `transform` animates
         nothing and the bar fades on the spot instead of leaving. */
      /* lg: is where the layout gives registration its own column. Above that
         the panel is already beside the artwork and a bar would be noise. */
      className={`w-full max-w-[28rem] min-w-0 transition-[translate,opacity] duration-300 ease-out motion-reduce:transition-none lg:hidden ${
        away
          ? /* Past the column's own bottom padding, so no sliver is left
               peeking above the safe area. */
            'pointer-events-none translate-y-[calc(100%+2rem)] opacity-0'
          : 'pointer-events-auto translate-y-0 opacity-100'
      }`}
    >
      {/*
       * NOTHING TO NAME MEANS NO CARD TO NAME IT IN.
       *
       * An event with several ways in fills the left of this bar with what
       * you can sign up as, and the card is the thing holding the two halves
       * together. A stock event has one registration and nothing to list, so
       * the card sat there as a white plank with a button in one corner and
       * dead space in the other.
       *
       * So on those it is not a card, it is a button: full width, same
       * height, same place. One control that is obviously one control.
       */}
      {ways.shown.length > 0 ? (
        <button
          type="button"
          onClick={onOpen}
          className="dock-rise border-line bg-paper/95 shadow-lift flex w-full min-w-0 items-center gap-3 rounded-2xl border px-4 py-3 text-left backdrop-blur"
        >
          {/* WHAT you can sign up as, and nothing else. A price here said
              neither of the two things the bar is for: it does not tell
              anybody that serving is part of this, and it cannot be right for
              four ways in that cost different amounts. The words do both. Two
              lines, then it stops. */}
          <span className="text-navy [display:-webkit-box] min-w-0 flex-1 [overflow:hidden] text-sm leading-snug font-extrabold [-webkit-box-orient:vertical] [-webkit-line-clamp:2]">
            {ways.shown.join(' · ')}
            {ways.more > 0 && <span className="text-slate font-semibold"> +{ways.more}</span>}
          </span>
          <span className="btn btn-primary shrink-0 !py-3 max-[380px]:!px-4">
            <Ticket className="h-5 w-5" />
            {label}
          </span>
        </button>
      ) : (
        <button
          type="button"
          onClick={onOpen}
          className="dock-rise btn btn-primary shadow-lift w-full !py-4 !text-base"
        >
          <Ticket className="h-5 w-5" />
          {label}
        </button>
      )}
    </div>,
    stack
  );
}
